import { ChevronDownIcon, ChevronLeftIcon } from "@heroicons/react/20/solid";
import { ActivityRecordType, RegionId, UserId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQueries, useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import {
    ActivityFeedRowBody,
    ActivityRowParentLink,
    ActivityRowsList,
} from "components/details-page-v-next/activity-rows";
import { recentActivityItemDisplay } from "components/details-page-v-next/recent-activities-feed";
import { GoalToggleButton } from "components/marketing-goals/goal-toggle-button";
import { PriorPeriodFooter } from "components/marketing-goals/prior-period-footer";
import {
    activityDayLabelWithYear,
    groupActivityByDate,
} from "components/marketing-plan/my-activity";
import { trpc } from "components/rpc/trpc";
import { ActivityTypeOption } from "components/tasks/activity-type-options";
import { BlankSlate } from "components/ui/blank-slate";
import { SecondaryCompactButtonLink } from "components/ui/button-link";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { ListSection } from "components/ui/list-section";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { Spinner } from "components/ui/spinner";
import React, { useMemo, useState } from "react";
import { hrefForActivityRecord } from "util/activity-record-href";
import { pluralize } from "util/pluralize";
import { ActivityColumnIcon } from "./activities-leaderboard";
import {
    ActivitiesReportColumnFilter,
    ActivitiesReportCount,
    ActivitiesReportFilters,
    ActivityColumn,
    activityColumnsFor,
    columnTotal,
    organizationTypesForQuery,
    overallTotal,
    reportQueryOptions,
    toActivitiesReportCounts,
} from "./activities-report";
import { CountModeToggle, RecordTypeFilter } from "./activities-report-filters";
import {
    ActivitiesExportBanner,
    ExportActivitiesButton,
    useActivitiesExport,
} from "./export-activities-button";

type RouterOutput = inferRouterOutputs<AppRouter>;
type StaffActivityItem =
    RouterOutput["activitiesReport"]["staffActivity"]["items"][number];

const feedPageSize = 50;

const restrictedRecordBody = "Activity on a record you don't have access to";

const allColumns: ActivitiesReportColumnFilter = { kind: "all" };

interface DateSpan {
    startDate: string;
    endDate: string;
}

interface CountTileProps {
    column: ActivityColumn;
    count: number;
    prior: number;
    priorLabel: string;
    selected: boolean;
    onToggle: () => void;
}

const CountTile: React.FC<CountTileProps> = props => (
    <GoalToggleButton
        selected={props.selected}
        onToggle={props.onToggle}
        className="min-w-0 rounded-lg px-3 py-2.5">
        <div className="flex items-center gap-1.5" title={props.column.hint ?? undefined}>
            <ActivityColumnIcon
                iconKind={props.column.iconKind}
                className="h-4 w-4 flex-shrink-0 text-gray-500"
            />
            <span className="truncate text-xs font-semibold text-zinc-800">
                {props.column.label}
            </span>
        </div>
        <div className="mt-1.5 text-xl font-bold leading-none tabular-nums text-zinc-950">
            {props.count}
        </div>
        <div className="mt-1.5 text-[11px] text-gray-500">
            <PriorPeriodFooter
                label={props.priorLabel}
                actual={props.count}
                prior={props.prior}
            />
        </div>
    </GoalToggleButton>
);

const StaffActivityRow: React.FC<{ item: StaffActivityItem }> = ({ item }) => {
    const display = recentActivityItemDisplay({
        id: item.id,
        kind: item.kind,
        label: item.label,
        chipLabel: item.chipLabel,
        category: item.category,
        text: item.restricted ? restrictedRecordBody : item.text,
        noteText: item.noteText,
        userName: null,
        occurredAt: item.time ? `${item.date}T${item.time}` : item.date,
    });
    const link =
        item.parentEntityId && item.parentName ? (
            <ActivityRowParentLink
                href={hrefForActivityRecord(item.recordType, item.parentEntityId)}
                parentName={item.parentName}
                recordType={item.recordType}
            />
        ) : null;
    return <ActivityFeedRowBody display={{ ...display, link }} actions={null} />;
};

interface StaffActivityFeedProps {
    regionId: RegionId | null;
    staffUserId: UserId;
    span: DateSpan;
    filters: ActivitiesReportFilters;
    columnFilter: ActivitiesReportColumnFilter;
    emptyMessage: string;
}

interface FeedPageState {
    isLoading: boolean;
    isError: boolean;
    isFetching: boolean;
    refetch: () => Promise<unknown>;
}

interface ShowMoreActivityProps {
    laterPages: FeedPageState[];
    remaining: number;
    onShowMore: () => void;
}

const ShowMoreActivity: React.FC<ShowMoreActivityProps> = props => {
    const failedPage = props.laterPages.find(page => page.isError);
    if (failedPage) {
        return (
            <RetryableErrorBanner
                message="Failed to load more activity."
                onRetry={() => void failedPage.refetch()}
                retrying={failedPage.isFetching}
            />
        );
    }
    return (
        <div className="flex justify-center">
            {props.laterPages.some(page => page.isLoading) ? (
                <Spinner />
            ) : (
                <InteractiveTextLink
                    className="inline-flex items-center gap-1 text-sm font-medium"
                    onClick={props.onShowMore}>
                    <ChevronDownIcon aria-hidden="true" className="h-4 w-4" />
                    Show more ({props.remaining} left)
                </InteractiveTextLink>
            )}
        </div>
    );
};

const StaffActivityFeed: React.FC<StaffActivityFeedProps> = props => {
    const trpcClient = trpc.useTRPC();
    const [pageCount, setPageCount] = useState(1);
    const pages = useQueries({
        queries: Array.from({ length: pageCount }, (_, pageIndex) => ({
            ...trpcClient.activitiesReport.staffActivity.queryOptions({
                regionId: props.regionId ?? ("" as RegionId),
                startDate: props.span.startDate,
                endDate: props.span.endDate,
                recordTypes: props.filters.recordTypes,
                organizationTypes: organizationTypesForQuery(
                    props.filters.organizationTypes,
                ),
                staffUserId: props.staffUserId,
                columnFilter: props.columnFilter,
                limit: feedPageSize,
                offset: pageIndex * feedPageSize,
            }),
            enabled: !!props.regionId,
            ...reportQueryOptions(),
        })),
    });
    const items = pages.flatMap(page => page.data?.items ?? []);
    const total = pages[0]?.data?.total ?? 0;
    const days = groupActivityByDate(items);

    if (pages[0]?.isLoading) {
        return (
            <div className="flex justify-center rounded-lg bg-white p-12 ring-1 ring-gray-200">
                <Spinner />
            </div>
        );
    }
    if (pages[0]?.isError) {
        return (
            <RetryableErrorBanner
                message="Failed to load activity."
                onRetry={() => void pages[0].refetch()}
                retrying={pages[0].isFetching}
            />
        );
    }
    if (items.length === 0) {
        return <BlankSlate>{props.emptyMessage}</BlankSlate>;
    }
    return (
        <>
            {days.map(day => (
                <ListSection
                    key={day.date}
                    label={activityDayLabelWithYear(day.date)}
                    tone="accent"
                    count={day.items.length}
                    disclosure={{ kind: "static" }}>
                    <ActivityRowsList
                        className="px-2 sm:px-4"
                        testId={`activities-report-${day.date}`}>
                        {day.items.map(item => (
                            <li key={`${item.kind}:${item.id}`}>
                                <StaffActivityRow item={item} />
                            </li>
                        ))}
                    </ActivityRowsList>
                </ListSection>
            ))}
            {items.length < total ? (
                <ShowMoreActivity
                    laterPages={pages.slice(1)}
                    remaining={total - items.length}
                    onShowMore={() => setPageCount(count => count + 1)}
                />
            ) : null}
        </>
    );
};

interface StaffActivityDetailProps {
    regionId: RegionId | null;
    staffUserId: UserId;
    current: DateSpan;
    prior: DateSpan;
    priorLabel: string;
    filters: ActivitiesReportFilters;
    availableRecordTypes: ActivityRecordType[];
    onFiltersChange: (filters: ActivitiesReportFilters) => void;
    activityTypes: ActivityTypeOption[];
    backHref: string;
}

export const StaffActivityDetail: React.FC<StaffActivityDetailProps> = props => {
    const trpcClient = trpc.useTRPC();
    const [selectedColumnKey, setSelectedColumnKey] = useState<string | null>(null);
    const activitiesExport = useActivitiesExport();
    const countsInput = (span: DateSpan) => ({
        regionId: props.regionId ?? ("" as RegionId),
        startDate: span.startDate,
        endDate: span.endDate,
        recordTypes: props.filters.recordTypes,
        organizationTypes: organizationTypesForQuery(props.filters.organizationTypes),
        staffUserIds: [props.staffUserId],
    });
    const currentQuery = useQuery({
        ...trpcClient.activitiesReport.counts.queryOptions(countsInput(props.current)),
        enabled: !!props.regionId,
        ...reportQueryOptions(),
    });
    const priorQuery = useQuery({
        ...trpcClient.activitiesReport.counts.queryOptions(countsInput(props.prior)),
        enabled: !!props.regionId,
        ...reportQueryOptions(),
    });
    const current: ActivitiesReportCount[] = useMemo(
        () => toActivitiesReportCounts(currentQuery.data ?? []),
        [currentQuery.data],
    );
    const prior: ActivitiesReportCount[] = useMemo(
        () => toActivitiesReportCounts(priorQuery.data ?? []),
        [priorQuery.data],
    );
    const columns = useMemo(
        () =>
            activityColumnsFor(
                props.filters.countMode,
                [...current, ...prior],
                props.activityTypes,
                props.filters.recordTypes,
            ),
        [
            props.filters.countMode,
            current,
            prior,
            props.activityTypes,
            props.filters.recordTypes,
        ],
    );
    const selectedColumn = columns.find(column => column.key === selectedColumnKey);
    const countsFailed = currentQuery.isError || priorQuery.isError;
    const currentTotal = overallTotal(current);
    const exportTotal = selectedColumn
        ? columnTotal(current, selectedColumn)
        : currentTotal;
    const countsLoaded =
        !countsFailed && !currentQuery.isLoading && !priorQuery.isLoading;
    const priorTotal = overallTotal(prior);
    const feedKey = [
        props.regionId ?? "",
        props.current.startDate,
        props.current.endDate,
        props.filters.recordTypes.join(","),
        props.filters.organizationTypes.join(","),
        selectedColumn?.key ?? "all",
    ].join("|");

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-end justify-between gap-2">
                <SecondaryCompactButtonLink
                    aria-label="Back to all staff members"
                    to={props.backHref}>
                    <ChevronLeftIcon className="size-4" /> All staff members
                </SecondaryCompactButtonLink>
                <CountModeToggle
                    value={props.filters.countMode}
                    onChange={countMode => {
                        setSelectedColumnKey(null);
                        props.onFiltersChange({ ...props.filters, countMode });
                    }}
                />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <RecordTypeFilter
                    filters={props.filters}
                    availableRecordTypes={props.availableRecordTypes}
                    onChange={filters => {
                        setSelectedColumnKey(null);
                        props.onFiltersChange(filters);
                    }}
                />
                {props.regionId && countsLoaded && exportTotal > 0 ? (
                    <ExportActivitiesButton
                        exportRequest={activitiesExport}
                        scope={{
                            regionId: props.regionId,
                            startDate: props.current.startDate,
                            endDate: props.current.endDate,
                            filters: {
                                ...props.filters,
                                staffUserIds: [props.staffUserId],
                            },
                            columnFilter: selectedColumn
                                ? selectedColumn.columnFilter
                                : allColumns,
                        }}
                    />
                ) : null}
            </div>
            <div className="empty:hidden">
                <ActivitiesExportBanner exportRequest={activitiesExport} />
            </div>
            <section className="space-y-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Activity
                </div>
                {countsFailed ? (
                    <RetryableErrorBanner
                        message="Failed to load activity counts."
                        onRetry={() => {
                            void currentQuery.refetch();
                            void priorQuery.refetch();
                        }}
                        retrying={currentQuery.isFetching || priorQuery.isFetching}
                    />
                ) : (
                    <div className="rounded-lg bg-white ring-1 ring-gray-200">
                        {currentQuery.isLoading || priorQuery.isLoading ? (
                            <div className="flex justify-center p-6">
                                <Spinner />
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-2 p-3 sm:px-4 lg:grid-cols-3 xl:grid-cols-4">
                                {columns.map(column => (
                                    <CountTile
                                        key={column.key}
                                        column={column}
                                        count={columnTotal(current, column)}
                                        prior={columnTotal(prior, column)}
                                        priorLabel={props.priorLabel}
                                        selected={column.key === selectedColumnKey}
                                        onToggle={() =>
                                            setSelectedColumnKey(key =>
                                                key === column.key ? null : column.key,
                                            )
                                        }
                                    />
                                ))}
                            </div>
                        )}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 text-xs text-gray-500 sm:px-4">
                            {selectedColumn ? (
                                <>
                                    <span>
                                        Showing{" "}
                                        <span className="font-semibold text-gray-700">
                                            {columnTotal(current, selectedColumn)}
                                        </span>{" "}
                                        {pluralize(
                                            columnTotal(current, selectedColumn),
                                            "activity",
                                            "activities",
                                        )}
                                    </span>
                                    <InteractiveTextLink
                                        className="text-xs font-medium"
                                        onClick={() => setSelectedColumnKey(null)}>
                                        Clear filter
                                    </InteractiveTextLink>
                                </>
                            ) : (
                                <span>
                                    Showing all{" "}
                                    <span className="font-semibold text-gray-700">
                                        {currentTotal}
                                    </span>{" "}
                                    {pluralize(currentTotal, "activity", "activities")}.{" "}
                                    {props.priorLabel}:{" "}
                                    <span className="font-semibold text-gray-700">
                                        {priorTotal}
                                    </span>
                                    . Select a tile above to filter.
                                </span>
                            )}
                        </div>
                    </div>
                )}
                <StaffActivityFeed
                    key={feedKey}
                    regionId={props.regionId}
                    staffUserId={props.staffUserId}
                    span={props.current}
                    filters={props.filters}
                    columnFilter={
                        selectedColumn ? selectedColumn.columnFilter : allColumns
                    }
                    emptyMessage={
                        currentTotal === 0
                            ? "No activity recorded in this date range. Emails, calls, texts, visits, notes, and completed activities show up here."
                            : "No activity matches the selected filters."
                    }
                />
            </section>
        </div>
    );
};
